import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'bg', label: 'BG', flag: '🇧🇬', name: 'Български' },
  { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English' },
  { code: 'ar', label: 'AR', flag: '🇸🇦', name: 'العربية' },
  { code: 'fr', label: 'FR', flag: '🇫🇷', name: 'Français' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const isRtl = i18n.language === 'ar';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRtl]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#111418]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-leaf-line text-yellow-400 text-2xl"></i>
          </div>
          <span className="text-white font-bold text-lg tracking-tight">WheatVest</span>
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {(['about', 'packages', 'calculator', 'contacts'] as const).map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className="text-white/70 hover:text-yellow-400 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              {t(`nav_${item}`)}
            </button>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/10 hover:border-white/25 cursor-pointer whitespace-nowrap"
            >
              <span>{currentLang.flag}</span>
              <span>{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {langOpen && (
              <div className="absolute top-full mt-2 right-0 bg-[#1a2020] border border-white/10 rounded-xl overflow-hidden min-w-[140px] z-50">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === lang.code
                        ? 'bg-yellow-500/15 text-yellow-400'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span>{lang.name}</span>
                    {i18n.language === lang.code && (
                      <i className="ri-check-line text-yellow-400 ml-auto"></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => scrollTo('packages')}
            className="border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-[#111418] transition-all px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap hidden sm:block"
          >
            {t('nav_start')}
          </button>

          <button
            className="md:hidden text-white/70 hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#111418]/98 backdrop-blur-md border-t border-white/10 px-4 py-4 flex flex-col gap-3">
          {(['about', 'packages', 'calculator', 'contacts'] as const).map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className="text-white/70 hover:text-yellow-400 transition-colors text-sm cursor-pointer text-left py-1"
            >
              {t(`nav_${item}`)}
            </button>
          ))}
          <div className="flex flex-wrap gap-2 mt-1">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-colors ${
                  i18n.language === lang.code
                    ? 'border-yellow-400 text-yellow-400 bg-yellow-400/10'
                    : 'border-white/10 text-white/50 hover:border-white/25 hover:text-white'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => scrollTo('packages')}
            className="border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-[#111418] transition-all px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap w-fit mt-1"
          >
            {t('nav_start')}
          </button>
        </div>
      )}
    </nav>
  );
}
